import type { MouseEvent as ReactMouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { PortalUnavailableCard } from '@/components/client-portal/PortalUnavailableCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { OrgAvatar } from '@/components/ui/org-avatar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useMyHostOrgsQuery } from '@/hooks/queries/useExternalOrgsQuery'
import {
  ExtendedOrganization,
  useOrganizationsQuery,
} from '@/hooks/queries/useOrganizationsQuery'
import { queryKeys } from '@/hooks/query-keys'
import { useIsMobile } from '@/hooks/use-mobile'
import { useMyEntities } from '@/hooks/useMyEntities'
import { clearOrganizationContextCache } from '@/lib/api/api-client'
import { FOCUS_RING } from '@/lib/focus-ring'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { handOffToOrganization } from '@/lib/utils/organization-switch'
import { isTauri, openWorkspaceWindow } from '@/lib/utils/tauri'
import { useAuth } from '@/store/auth.store'
import { useEntityStore } from '@/store/entity'
import { useOrganizationStore } from '@/store/organization.store'
import { useTaskState } from '@/store/taskState'
import { useTodayLayout } from '@/store/today-layout.store'
import { useUserPreferencesStore } from '@/store/user-preferences.store'
import { useUserProfileStore } from '@/store/user-profile.store'
import {
  ArrowSquareOutIcon,
  BuildingsIcon,
  CaretUpDownIcon,
  CheckIcon,
  GearIcon,
} from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import {
  AccountType,
  ExternalOrgRelationship,
  Organization,
} from '@scrambl/shared'

const logger = createLogger('components/layout/OrganizationSwitcher')

type OrgData = Organization

// Extended type for display purposes
interface DisplayOrganization extends Organization {
  isExternal?: boolean
  relationshipId?: string
  logoUrl?: string | null
  // Store the full relationship for permission checking
  externalRelationship?: ExternalOrgRelationship
}

interface OrganizationSwitcherProps {
  className?: string
  compact?: boolean
  /**
   * `header` (default): the title-bar pill. `rail`: the row at the top of
   * the sidebar — the space's avatar alone at icon-rail width, with its name
   * beside it once the rail is wide enough (`expanded`). The sidebar passes
   * its own row geometry and theme colors through `className`.
   */
  placement?: 'header' | 'rail'
  /** Rail placement: the rail is wide enough to show the space's name. */
  expanded?: boolean
  /** Rail placement: name the space in a tooltip while the rail is icon-only. */
  tooltip?: boolean
  /** Rail placement: the sidebar holds its hover expansion while the menu is open. */
  onOpenChange?: (open: boolean) => void
}

// Async function to clear all organization-specific stores
function clearOrganizationSpecificStores() {
  try {
    // Clear entity store
    const entityStore = useEntityStore.getState()
    if (typeof entityStore.reset === 'function') {
      entityStore.reset()
    }

    // Deal state is managed by TanStack Query - cache invalidation handles cleanup

    // Contact state is managed by TanStack Query - cache invalidation handles cleanup

    // Project state is managed by TanStack Query - cache invalidation handles cleanup

    const taskStore = useTaskState.getState()
    // Call the full reset method to clear all task data
    try {
      if (typeof taskStore.reset === 'function') {
        taskStore.reset()
      }
    } catch (error) {
      logger.warn('[OrganizationSwitcher] Could not reset task store:', error)
    }

    // Clear task selection store
    try {
      if (typeof taskStore.clearSelection === 'function') {
        taskStore.clearSelection()
      }
    } catch (error) {
      logger.warn(
        '[OrganizationSwitcher] Could not clear task selection store:',
        error,
      )
    }

    // Clear task name edit store
    try {
      if (typeof taskStore.setEditingTask === 'function') {
        taskStore.setEditingTask(null)
      }
    } catch (error) {
      logger.warn(
        '[OrganizationSwitcher] Could not clear task name edit store:',
        error,
      )
    }

    // Clear dashboard layout store
    try {
      // Reset dashboard layout completely (clears layout and resets fetch flag)
      useTodayLayout.getState().reset()
    } catch (error) {
      logger.warn(
        '[OrganizationSwitcher] Could not clear dashboard layout store:',
        error,
      )
    }
  } catch (error) {
    logger.warn('[OrganizationSwitcher] Error clearing some stores:', error)
  }
}

export function OrganizationSwitcher({
  className,
  compact = false,
  placement = 'header',
  expanded = false,
  tooltip = false,
  onOpenChange,
}: OrganizationSwitcherProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [open, setOpenState] = useState(false)
  const isRail = placement === 'rail'
  const setOpen = (next: boolean) => {
    setOpenState(next)
    onOpenChange?.(next)
  }
  const [forceRender, setForceRender] = useState(0)
  const hasInitializedRef = useRef(false)
  const logoFetchAttemptsRef = useRef<Record<string, boolean>>({})
  const isMobile = useIsMobile()
  const showWorkspaceWindowActions = isTauri() && !isMobile

  const { currentOrganization } = useOrganizationStore()

  const { resetOrganizationData } = useUserProfileStore()
  const { isAuthenticated } = useAuth()
  const { clearPreferences: clearUserPreferences } = useUserPreferencesStore()

  // Check if organizations data is already in cache (from MainLayout)
  // This avoids triggering a duplicate fetch - MainLayout is the primary fetcher
  const cachedOrganizations = queryClient.getQueryData<ExtendedOrganization[]>(
    queryKeys.organizations.list({ withLogos: true }),
  )

  // Only enable the query if there's no cached data (fallback for edge cases)
  const { data: queryOrganizations = [], isPending: isPendingOrganizations } =
    useOrganizationsQuery({
      enabled: isAuthenticated && !cachedOrganizations,
    })

  // Use cached data if available, otherwise use query data
  const organizations = cachedOrganizations || queryOrganizations

  // Fetch external organizations (host orgs the current user can access)
  // This uses user-level access, not organization-level
  const {
    data: myHostOrgs = [],
    isLoading: isLoadingHostOrgs,
    error: hostOrgsError,
  } = useMyHostOrgsQuery({
    enabled: isAuthenticated,
  })

  // Client portal entities the user has guest access to
  const directoryQuery = useMyEntities()
  const { data: myEntitiesData, isLoading: isLoadingMyEntities } =
    directoryQuery
  const portalEntities = myEntitiesData?.entities ?? []
  const activePortalEntityId = location.pathname.match(
    /^\/client-portal\/([^/]+)(?:\/|$)/,
  )?.[1]
  const activeOrganizationSlug = activePortalEntityId
    ? undefined
    : location.pathname.split('/').filter(Boolean)[0]

  useEffect(() => {
    // No need to fetch organizations here - they should already be fetched by the app initialization
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true
    }
  }, [isAuthenticated, organizations.length])

  // Convert host orgs to display format
  const externalOrganizations: DisplayOrganization[] = myHostOrgs.map(
    relationship => ({
      id: relationship.hostOrganization?.id || relationship.hostOrganizationId,
      name: relationship.hostOrganization?.name || 'Unknown Organization',
      slug: relationship.hostOrganization?.slug || '',
      logo: relationship.hostOrganization?.logo || null,
      logoUrl: relationship.hostOrganization?.logo || null,
      isExternal: true,
      relationshipId: relationship.id,
      externalRelationship: relationship, // Store full relationship for permission checking
      createdAt: new Date(),
      updatedAt: new Date(),
    }),
  )

  // Combine regular orgs (only those where the user has a real membership) with
  // external workspaces. Orgs the user can only reach as a client-portal guest
  // are surfaced separately in the "Client Portals" group below.
  const membershipOrgs = organizations.filter(org =>
    Boolean(org.userMembership),
  )
  const allOrganizations: DisplayOrganization[] = [
    ...membershipOrgs.map(org => ({ ...org, isExternal: false })),
    ...externalOrganizations.filter(
      extOrg => !membershipOrgs.some(org => org.id === extOrg.id),
    ),
  ]

  const handleOrgSelect = (org: DisplayOrganization) => {
    if (!org.id || !org.slug) {
      toast.error('Organization has no slug. Please contact support.')
      return
    }

    setOpen(false)

    // Hand off with a single document load. Every org-scoped store, cache and
    // socket room is rebuilt for the target organization, so nothing has to be
    // torn down here — and there is no window in which the store and the URL
    // disagree about which organization is active.
    // The OrgRouteGuard still handles subscription checking on the way in.
    handOffToOrganization(org.id, `/${org.slug}/today`)
  }

  const handlePortalSelect = (portalEntityId: string) => {
    resetOrganizationData()
    clearUserPreferences()
    clearOrganizationSpecificStores()
    try {
      clearOrganizationContextCache()
    } catch (error) {
      logger.warn(
        '[OrganizationSwitcher] Could not clear API client cache:',
        error,
      )
    }
    navigate(`/client-portal/${portalEntityId}`)
    setOpen(false)
  }

  const handleGoToOrganizations = () => {
    navigate('/organizations')
    setOpen(false)
  }

  const handleOpenWorkspaceWindow = async (
    event: ReactMouseEvent<HTMLButtonElement>,
    contextKey: string,
    path: string,
    title: string,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen(false)

    const windowId = await openWorkspaceWindow(contextKey, path, title)
    if (!windowId) {
      toast.error('Could not open the workspace window.')
    }
  }

  const renderOpenWorkspaceWindowButton = (
    contextKey: string,
    path: string,
    title: string,
    accessibleName: string,
  ) => {
    if (!showWorkspaceWindowActions) return null

    return (
      <button
        type="button"
        aria-label={accessibleName}
        title={accessibleName}
        onPointerDown={event => {
          event.preventDefault()
          event.stopPropagation()
        }}
        onClick={event => {
          void handleOpenWorkspaceWindow(event, contextKey, path, title)
        }}
        className={cn(
          'ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background/80 hover:text-primary',
          FOCUS_RING,
        )}
      >
        <ArrowSquareOutIcon className="h-3.5 w-3.5" weight="bold" />
      </button>
    )
  }

  // Separate regular orgs from external orgs for grouped display
  const regularOrgs = allOrganizations.filter(org => !org.isExternal)
  const externalOrgs = allOrganizations.filter(org => org.isExternal)
  // Personal spaces get their own section at the top; this split is informative
  // for business users too (and a business user may own a personal space), so
  // it's not account-type gated.
  const personalOrgs = regularOrgs.filter(
    org => org.accountType === AccountType.PERSONAL,
  )
  const businessOrgs = regularOrgs.filter(
    org => org.accountType !== AccountType.PERSONAL,
  )

  // Shared row renderer for the Personal and Organizations groups (identical
  // markup — only the grouping heading differs).
  const renderOrgCommandItem = (org: DisplayOrganization) => (
    <CommandItem
      key={`${org.id}-${forceRender}`}
      value={org.name}
      onSelect={() => handleOrgSelect(org)}
      className="transition-tonal group cursor-pointer rounded-lg px-2 py-2 duration-200 hover:!bg-primary/10 data-[highlighted]:!bg-primary/10 [&[aria-selected=true]]:!bg-primary/10 [&[data-selected=true]]:!bg-primary/10"
    >
      <div className="flex flex-1 items-center gap-2">
        <OrgAvatar
          organization={org}
          logoUrl={org.logoUrl || undefined}
          size="xs"
          className="transition-tonal duration-200"
        />
        <span className="truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
          {org.name}
        </span>
      </div>
      <CheckIcon
        className={cn(
          'transition-tonal ml-auto h-3.5 w-3.5 shrink-0 text-primary duration-200',
          activeOrganizationSlug === org.slug
            ? 'scale-100 opacity-100'
            : 'scale-75 opacity-0',
        )}
      />
      {renderOpenWorkspaceWindowButton(
        `organization-${org.id}`,
        `/${org.slug}/today`,
        org.name,
        `Open ${org.name} in a new window`,
      )}
    </CommandItem>
  )

  const renderPopoverContent = () => (
    <PopoverContent
      className="rounded-card w-[280px] bg-background p-0"
      align="start"
      // Beside the sidebar row; under the title-bar pill.
      side={isRail ? 'right' : 'bottom'}
      sideOffset={isRail ? 8 : undefined}
    >
      <Command className="bg-transparent">
        <CommandInput
          placeholder="Search spaces..."
          className="h-9 bg-background/50 text-sm transition-colors placeholder:text-muted-foreground/60 focus:border-primary/50"
        />
        {directoryQuery.unavailable && (
          <PortalUnavailableCard
            title="Client spaces"
            retrying={directoryQuery.isFetching}
            onRetry={() => void directoryQuery.refetch().catch(() => {})}
          />
        )}
        <CommandList className="max-h-[320px] overflow-y-auto">
          <CommandEmpty className="py-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted/20">
                <BuildingsIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  No spaces found
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Try a different search term
                </p>
              </div>
            </div>
          </CommandEmpty>

          {/* Personal spaces */}
          {personalOrgs.length > 0 && (
            <CommandGroup heading="Personal" className="p-1">
              {personalOrgs.map(org => renderOrgCommandItem(org))}
            </CommandGroup>
          )}

          {/* Regular Organizations */}
          {businessOrgs.length > 0 && (
            <>
              {personalOrgs.length > 0 && (
                <div className="mx-2 h-px bg-border/50" />
              )}
              <CommandGroup heading="Organizations" className="p-1">
                {businessOrgs.map(org => renderOrgCommandItem(org))}
              </CommandGroup>
            </>
          )}

          {/* Client Portals */}
          {portalEntities.length > 0 && (
            <>
              <div className="mx-2 h-px bg-border/50" />
              <CommandGroup heading="Client Portals" className="p-1">
                {portalEntities.map(entity => (
                  <CommandItem
                    key={`portal-${entity.entityId}`}
                    value={`${entity.organization.name} ${entity.entityName} client portal`}
                    onSelect={() => handlePortalSelect(entity.entityId)}
                    className="transition-tonal group cursor-pointer rounded-lg px-2 py-2 duration-200 hover:!bg-primary/10 data-[highlighted]:!bg-primary/10 [&[aria-selected=true]]:!bg-primary/10 [&[data-selected=true]]:!bg-primary/10"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <OrgAvatar
                        organization={entity.organization}
                        logoUrl={entity.organization.logoUrl}
                        size="xs"
                        className="transition-tonal shrink-0 duration-200"
                      />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                          {entity.organization.name}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {entity.entityName}
                        </span>
                      </div>
                    </div>
                    <CheckIcon
                      className={cn(
                        'transition-tonal ml-auto h-3.5 w-3.5 shrink-0 text-primary duration-200',
                        activePortalEntityId === entity.entityId
                          ? 'scale-100 opacity-100'
                          : 'scale-75 opacity-0',
                      )}
                    />
                    {renderOpenWorkspaceWindowButton(
                      `portal-${entity.entityId}`,
                      `/client-portal/${entity.entityId}`,
                      `${entity.organization.name} — ${entity.entityName}`,
                      `Open ${entity.organization.name} ${entity.entityName} client portal in a new window`,
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* External Organizations */}
          {externalOrgs.length > 0 && (
            <>
              <div className="mx-2 h-px bg-border/50" />
              <CommandGroup heading="External Workspaces" className="p-1">
                {externalOrgs.map(org => (
                  <CommandItem
                    key={`external-${org.id}-${forceRender}`}
                    value={`${org.name} external`}
                    onSelect={() => handleOrgSelect(org)}
                    className="transition-tonal group cursor-pointer rounded-lg px-2 py-2 duration-200 hover:!bg-primary/10 data-[highlighted]:!bg-primary/10 [&[aria-selected=true]]:!bg-primary/10 [&[data-selected=true]]:!bg-primary/10"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <div className="relative shrink-0">
                        <OrgAvatar
                          organization={org}
                          logoUrl={org.logoUrl || undefined}
                          size="xs"
                          className="transition-tonal duration-200"
                        />
                        {/* External indicator icon - building to indicate organization */}
                        <div className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full border border-background bg-blue-500">
                          <BuildingsIcon
                            weight="fill"
                            className="h-2 w-2 text-white"
                          />
                        </div>
                      </div>
                      <span className="truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                        {org.name}
                      </span>
                    </div>
                    <CheckIcon
                      className={cn(
                        'transition-tonal ml-1 h-3.5 w-3.5 shrink-0 text-primary duration-200',
                        activeOrganizationSlug === org.slug
                          ? 'scale-100 opacity-100'
                          : 'scale-75 opacity-0',
                      )}
                    />
                    {renderOpenWorkspaceWindowButton(
                      `organization-${org.id}`,
                      `/${org.slug}/today`,
                      org.name,
                      `Open ${org.name} in a new window`,
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          <div className="mx-2 h-px bg-border/50" />
          <CommandGroup className="p-1">
            <CommandItem
              onSelect={handleGoToOrganizations}
              className="transition-tonal group cursor-pointer rounded-lg px-2 py-2 text-sm text-foreground duration-200 hover:!bg-primary/10 data-[highlighted]:!bg-primary/10 [&[aria-selected=true]]:!bg-primary/10 [&[data-selected=true]]:!bg-primary/10"
            >
              <div className="transition-tonal mr-2 flex h-5 w-5 items-center justify-center rounded-lg bg-muted/20 duration-200 group-hover:bg-primary/10">
                <GearIcon className="h-3 w-3" />
              </div>
              <span className="text-sm font-medium">Manage Spaces</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  )

  // On a cold boot the space lists are still in flight, so the switcher used
  // to render nothing and the header reflowed the moment they landed. Hold a
  // stable-width placeholder while they resolve; a genuinely space-less user
  // still gets nothing once the queries settle.
  const isResolvingSpaces =
    isAuthenticated &&
    ((!cachedOrganizations && isPendingOrganizations) ||
      isLoadingHostOrgs ||
      isLoadingMyEntities)

  if (
    !isAuthenticated ||
    (allOrganizations.length === 0 && portalEntities.length === 0)
  ) {
    if (isResolvingSpaces) {
      if (isRail) {
        // Hold the row's avatar slot so the rail doesn't reflow.
        return (
          <div aria-hidden className={cn('flex items-center', className)}>
            <Skeleton className="rounded-control h-8 w-8 shrink-0 bg-current opacity-10" />
          </div>
        )
      }
      return (
        <div
          aria-hidden
          className={cn(
            'flex items-center',
            compact ? 'h-6' : 'h-8',
            isMobile ? 'w-full' : 'w-[150px]',
            className,
          )}
        >
          <Skeleton className="h-full w-full rounded-lg bg-muted/30" />
        </div>
      )
    }
    if (isAuthenticated && directoryQuery.unavailable)
      return (
        <Button
          variant="ghost"
          size="sm"
          disabled={directoryQuery.isFetching}
          onClick={() => void directoryQuery.refetch().catch(() => {})}
        >
          Retry client spaces
        </Button>
      )
    return null
  }

  if (isRail) {
    const spaceName = currentOrganization?.name ?? 'Select space'
    // The same geometry at both rail widths as the rows under it: a 32px
    // logo filling the row (larger than the rows' 22px glyphs, as the
    // space's identity), the name fading in beside it as the rail widens
    // (never resizing, so nothing skews mid-transition).
    const trigger = (
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-label={`Switch space: ${spaceName}`}
        className={cn('no-animate', className)}
        key={`org-switcher-${forceRender}-${currentOrganization?.id || 'none'}`}
      >
        {/* The rail rows' 10px corner, so the logo and the rows under it
            share one shape. */}
        <OrgAvatar
          organization={currentOrganization}
          logoUrl={currentOrganization?.logoUrl || undefined}
          size="xs"
          shape="control"
          className={cn(
            'h-8 w-8 shrink-0 text-xs',
            !currentOrganization && 'bg-muted/20',
          )}
        />
        <span
          aria-hidden
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 overflow-hidden transition-opacity ease-out motion-reduce:transition-none',
            expanded
              ? 'opacity-100 delay-100 duration-200'
              : 'opacity-0 duration-100',
          )}
        >
          <span className="min-w-0 flex-1 truncate text-[13px] font-[560]">
            {spaceName}
          </span>
          <CaretUpDownIcon className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </span>
      </button>
    )
    return (
      <Popover open={open} onOpenChange={setOpen}>
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">{spaceName}</TooltipContent>
          </Tooltip>
        ) : (
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        )}
        {renderPopoverContent()}
      </Popover>
    )
  }

  // Header placement: the title-bar pill.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-8 justify-start px-2 transition-colors duration-200 hover:!bg-primary/10',
            compact ? 'h-6 px-1.5' : '',
            isMobile ? 'w-full' : 'w-auto max-w-[220px]',
            className,
          )}
          key={`org-switcher-${forceRender}-${currentOrganization?.id || 'none'}`}
        >
          <div
            className={cn('flex items-center', compact ? 'gap-1.5' : 'gap-2')}
          >
            {currentOrganization ? (
              <>
                <OrgAvatar
                  organization={currentOrganization}
                  logoUrl={currentOrganization.logoUrl || undefined}
                  size="xs"
                  className={cn(
                    'h-5 w-5 transition-all duration-200',
                    compact && 'h-4 w-4',
                  )}
                />
                <span
                  className={cn(
                    'truncate text-sm font-medium',
                    compact && 'text-xs',
                  )}
                >
                  {currentOrganization.name}
                </span>
              </>
            ) : (
              <>
                <OrgAvatar
                  organization={null}
                  size="xs"
                  className={cn('h-5 w-5 bg-muted/20', compact && 'h-4 w-4')}
                />
                <span
                  className={cn(
                    'text-sm font-medium text-muted-foreground',
                    compact && 'text-xs',
                  )}
                >
                  Select organization
                </span>
              </>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      {renderPopoverContent()}
    </Popover>
  )
}
